import { z } from 'zod';
import type { TFunction } from 'i18next';
import { FORM_FIELD_LIMITS } from '@/shared/utils/form-field-limits';

/**
 * Builds the zod schema for the create/edit-launch form with localized
 * validation messages.
 */
export function buildLaunchFormSchema(t: TFunction) {
  return z.object({
    name: z.string()
      .min(1, { message: t('create.errors.nameRequired') })
      .max(FORM_FIELD_LIMITS.launchName),
    address: z.string().max(FORM_FIELD_LIMITS.launchAddress).default(''),
    holdTtlMinutes: z.coerce.number()
      .min(1, { message: t('create.errors.ttlMinimum') })
      .max(FORM_FIELD_LIMITS.holdTtlMinutes)
      .default(15),
  });
}

export type CreateLaunchFormData = z.infer<ReturnType<typeof buildLaunchFormSchema>>;

/**
 * Schema for a single unit row in the inventory.
 */
export const unitRowSchema = z.object({
  property_id: z.string().default(''),
  property_label: z.string().default(''),
  label: z.string().max(FORM_FIELD_LIMITS.unitLabel),
  tower: z.string().max(FORM_FIELD_LIMITS.unitTower).default(''),
  floor: z.string().max(FORM_FIELD_LIMITS.unitFloor).default(''),
  typology: z.string().default(''),
  list_price: z.string().default(''),
});

export type UnitRowData = z.infer<typeof unitRowSchema>;

/**
 * Returns the trimmed string, or undefined when blank.
 */
function orUndefined(value: string): string | undefined {
  return value || undefined;
}

/**
 * Parses a masked price string into a number, or undefined when blank.
 */
function parseListPrice(value: string): number | undefined {
  return value ? Number(value.replace(/\D/g, '')) : undefined;
}

/**
 * Maps a local unit row to the API-expected unit input shape.
 * Shared by the create and edit launch flows.
 *
 * @param u - The local unit row data.
 * @returns The API-shaped unit input.
 */
export function mapUnitRowToInput(u: UnitRowData) {
  return {
    property_id: u.property_id || '',
    label: u.label,
    tower: orUndefined(u.tower),
    floor: orUndefined(u.floor),
    typology: orUndefined(u.typology),
    list_price: parseListPrice(u.list_price),
  };
}
